import { Button, DropdownMenu } from '@artosera/ui';
import { PLAFOND_LOT_OEUVRES, type ActionLotOeuvres } from '@artosera/shared';
import { ChevronDown, ChevronUp, Download } from 'lucide-react';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { dansLaPorteeCourante } from '../../../api/cles.js';
import { estApiError } from '../../../api/errors.js';
import { lotOeuvresApi } from '../../../api/lotOeuvres.js';
import type { FiltresOeuvres } from '../../../api/oeuvres.js';
import { galerieALaction, galerieAModule } from '../../../auth/sujet.js';
import { useEcranEtroit } from '../../../composants/calendrier/useEcranEtroit.js';
import { BoutonSerieCatalogage } from '../../catalogage/SerieCatalogage.js';
import { NouvelleSelection } from '../../presentation/NouvelleSelection.js';
import { useShellContext } from '../../shell/context.js';
import { EntreeEtiquettesLot } from './EntreeEtiquettesLot.js';
import { LotOeuvresDialog } from './LotOeuvresDialog.js';
import styles from './BandeauSelection.module.css';

/** Un geste du bandeau : une entrée de menu en bureau, un bouton pleine largeur au pouce. */
interface Geste {
  cle: string;
  libelle: string;
  onSelect: () => void;
  chargement?: boolean;
}

interface Groupe {
  cle: string;
  libelle: string;
  gestes: Geste[];
}

export interface BandeauSelectionProps {
  /** La sélection, dans l'ordre où elle s'est faite : c'est l'ordre du lot. */
  selection: readonly string[];
  /** Les œuvres de la page affichée. */
  idsPage: readonly string[];
  /** Le nombre d'œuvres de toute la recherche. */
  totalRecherche: number;
  /** Les filtres de la liste : « toute la recherche » les reprend tels quels. */
  filtres: FiltresOeuvres;
  onRemplacer: (ids: readonly string[]) => void;
  onDeselectionner: () => void;
  /** Absent quand le dossier PDF n'est pas permis ou que la sélection dépasse 30 œuvres. */
  onDossier?: (() => void) | undefined;
  /** Absent quand la liste de prix n'est pas permise. */
  onListeDePrix?: (() => void) | undefined;
  listeDePrixEnCours?: boolean;
}

/**
 * Le bandeau de la sélection multiple : collant en haut de la liste, il porte
 * le nombre d'œuvres, « toute la recherche » et les gestes groupés. Au
 * téléphone, c'est une barre basse avec un bouton « Actions ».
 */
export function BandeauSelection({
  selection,
  idsPage,
  totalRecherche,
  filtres,
  onRemplacer,
  onDeselectionner,
  onDossier,
  onListeDePrix,
  listeDePrixEnCours = false,
}: BandeauSelectionProps): JSX.Element {
  const { t } = useTranslation(['regie', 'presentation']);
  const { galerieSlug, galerie, compte } = useShellContext();
  const etroit = useEcranEtroit();
  const [panneauOuvert, setPanneauOuvert] = useState(false);
  const [lot, setLot] = useState<{ type: ActionLotOeuvres; ids: readonly string[] } | null>(null);
  const [rechercheEnCours, setRechercheEnCours] = useState(false);
  const [tronque, setTronque] = useState<number | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const tr = (cle: string, valeurs?: Record<string, unknown>): string =>
    t(`regie:oeuvres.lot.${cle}`, valeurs);

  const peutEcrire = galerieALaction(galerie, 'artworks:write');
  const peutArchiver = galerieALaction(galerie, 'artworks:archive');
  const peutPrix = galerieALaction(galerie, 'artworks:bulk-price');
  // L'API exige aussi `artworks:write` pour ces deux ajouts (docs/dev/lot-oeuvres.md).
  const peutExposition = peutEcrire && galerieALaction(galerie, 'exhibitions:write');
  const peutSelectionClient =
    peutEcrire &&
    galerieAModule(galerie, 'rendez-vous-client') &&
    galerieALaction(galerie, 'presentation:write');
  const trop = selection.length > PLAFOND_LOT_OEUVRES;

  const ouvrir = (type: ActionLotOeuvres) => () => {
    setPanneauOuvert(false);
    setLot({ type, ids: [...selection] });
  };

  const modifier: Geste[] = [];
  if (peutEcrire) {
    for (const type of ['statut', 'localisation', 'publication'] as const) {
      modifier.push({ cle: type, libelle: tr(`gestes.${type}`), onSelect: ouvrir(type) });
    }
  }
  if (peutPrix)
    modifier.push({ cle: 'prix', libelle: tr('gestes.prix'), onSelect: ouvrir('prix') });
  const ajouter: Geste[] = [];
  if (peutExposition) {
    ajouter.push({
      cle: 'exposition',
      libelle: tr('gestes.exposition'),
      onSelect: ouvrir('exposition'),
    });
  }
  if (peutSelectionClient) {
    ajouter.push({
      cle: 'selection',
      libelle: tr('gestes.selection'),
      onSelect: ouvrir('selection'),
    });
  }
  const documents: Geste[] = [];
  if (onListeDePrix) {
    documents.push({
      cle: 'liste-de-prix',
      libelle: t('regie:oeuvres.listeDePrixSelection.action'),
      onSelect: () => {
        setPanneauOuvert(false);
        onListeDePrix();
      },
      chargement: listeDePrixEnCours,
    });
  }
  if (onDossier) {
    documents.push({
      cle: 'dossier',
      libelle: t('regie:dossiersOeuvres.titre'),
      onSelect: () => {
        setPanneauOuvert(false);
        onDossier();
      },
    });
  }
  const groupes: Groupe[] = [
    { cle: 'modifier', libelle: tr('groupes.modifier'), gestes: modifier },
    { cle: 'ajouter', libelle: tr('groupes.ajouter'), gestes: ajouter },
    { cle: 'documents', libelle: tr('groupes.documents'), gestes: documents },
  ].filter((groupe) => groupe.gestes.length > 0);

  const toutePageCochee = idsPage.length > 0 && idsPage.every((id) => selection.includes(id));
  const proposeRecherche =
    toutePageCochee &&
    totalRecherche > idsPage.length &&
    selection.length < Math.min(totalRecherche, PLAFOND_LOT_OEUVRES);

  async function selectionnerLaRecherche(): Promise<void> {
    setErreur(null);
    setRechercheEnCours(true);
    try {
      const reponse = await dansLaPorteeCourante((appel) =>
        lotOeuvresApi.identifiants(galerieSlug, filtres, appel),
      );
      onRemplacer(reponse.ids);
      setTronque(reponse.tronque ? reponse.total : null);
    } catch (e) {
      setErreur(estApiError(e) ? e.message : t('commun:erreurReseau'));
    } finally {
      setRechercheEnCours(false);
    }
  }

  const gestesDuBureau = (
    <>
      {groupes.map((groupe) => (
        <DropdownMenu
          key={groupe.cle}
          align="start"
          trigger={
            <Button variant="secondary" size="sm" disabled={trop}>
              {groupe.libelle}
              <ChevronDown size={16} strokeWidth={1.5} aria-hidden />
            </Button>
          }
          entries={groupe.gestes.map((geste) => ({
            type: 'item' as const,
            item: { key: geste.cle, label: geste.libelle, onSelect: geste.onSelect },
          }))}
        />
      ))}
      {peutArchiver ? (
        <Button variant="secondary" size="sm" disabled={trop} onClick={ouvrir('archivage')}>
          {tr('gestes.archivage')}
        </Button>
      ) : null}
    </>
  );

  const gestesDuPouce = (
    <>
      {groupes.map((groupe) => (
        <section key={groupe.cle} className={styles.groupe} aria-label={groupe.libelle}>
          <h3 className={styles.groupeTitre}>{groupe.libelle}</h3>
          {groupe.gestes.map((geste) => (
            <button
              key={geste.cle}
              type="button"
              className={styles.geste}
              disabled={trop || geste.chargement === true}
              onClick={geste.onSelect}
            >
              {geste.cle === 'liste-de-prix' ? (
                <Download size={16} strokeWidth={1.5} aria-hidden />
              ) : null}
              {geste.libelle}
            </button>
          ))}
        </section>
      ))}
      {peutArchiver ? (
        <section className={styles.groupe} aria-label={tr('gestes.archivage')}>
          <button
            type="button"
            className={styles.geste}
            disabled={trop}
            onClick={ouvrir('archivage')}
          >
            {tr('gestes.archivage')}
          </button>
        </section>
      ) : null}
    </>
  );

  // Les gestes qui portent leur propre dialogue gardent leurs conditions.
  const gestesPropres = (
    <>
      <BoutonSerieCatalogage oeuvreIds={[...selection]} />
      <NouvelleSelection
        key={`${compte.id}:${galerieSlug}`}
        disabled={selection.length === 0 || selection.length > 200}
        origine={{ elements: selection.map((oeuvreId) => ({ oeuvreId })) }}
      />
      <EntreeEtiquettesLot oeuvreIds={selection} />
    </>
  );

  return (
    <div
      className={styles.bandeau}
      role="region"
      aria-label={tr('bandeau.libelle')}
      data-etroit={etroit ? 'true' : undefined}
    >
      {proposeRecherche ? (
        <div className={styles.recherche}>
          <span>{tr('bandeau.pageCochee', { count: idsPage.length })}</span>
          <Button
            variant="ghost"
            size="sm"
            loading={rechercheEnCours}
            onClick={() => void selectionnerLaRecherche()}
          >
            {tr('bandeau.toutSelectionner', { count: totalRecherche })}
          </Button>
        </div>
      ) : null}
      {tronque !== null ? (
        <p className={styles.note} role="status">
          {tr('bandeau.plafond', { plafond: PLAFOND_LOT_OEUVRES, total: tronque })}
        </p>
      ) : null}
      {trop ? (
        <p className={styles.note} role="status">
          {tr('bandeau.trop', { plafond: PLAFOND_LOT_OEUVRES })}
        </p>
      ) : null}
      {erreur ? (
        <p className={styles.erreur} role="alert">
          {erreur}
        </p>
      ) : null}

      <div className={styles.ligne}>
        <p className={styles.nombre}>
          <strong>{selection.length}</strong>{' '}
          {etroit
            ? tr('bandeau.nombreCourt', { count: selection.length })
            : tr('bandeau.nombre', { count: selection.length })}
        </p>
        <Button variant="ghost" size="sm" onClick={onDeselectionner}>
          {tr('bandeau.deselectionner')}
        </Button>
        {etroit ? (
          <Button
            size="sm"
            className={styles.boutonActions}
            aria-expanded={panneauOuvert}
            aria-controls="lot-actions-panneau"
            onClick={() => setPanneauOuvert((ouvert) => !ouvert)}
          >
            {tr('bandeau.actions')}
            {panneauOuvert ? (
              <ChevronDown size={16} strokeWidth={1.5} aria-hidden />
            ) : (
              <ChevronUp size={16} strokeWidth={1.5} aria-hidden />
            )}
          </Button>
        ) : (
          <div className={styles.gestes}>
            {gestesDuBureau}
            {gestesPropres}
          </div>
        )}
      </div>

      {etroit ? (
        <div id="lot-actions-panneau" className={styles.panneau} hidden={!panneauOuvert}>
          {gestesDuPouce}
          <div className={styles.propres}>{gestesPropres}</div>
        </div>
      ) : null}

      {lot ? (
        <LotOeuvresDialog
          type={lot.type}
          oeuvreIds={lot.ids}
          onAnnuler={() => setLot(null)}
          onTermine={(refusees) => {
            setLot(null);
            if (refusees) onRemplacer(refusees);
            else onDeselectionner();
          }}
        />
      ) : null}
    </div>
  );
}
